import { computed, Service, signal } from '@angular/core';
import { readStorage, writeStorage } from '../shared/utils/storage';
import { STORAGE_KEYS } from './constants';

export type Theme = 'light' | 'dark';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * base-100 обеих тем DaisyUI: в `<meta>` нет доступа к CSS-переменным, поэтому hex продублирован.
 * Источник значений — oklch(100% 0 0) и oklch(25.33% .016 252.42); тот же тёмный hex стоит в index.html.
 */
const THEME_COLORS: Record<Theme, string> = { light: '#ffffff', dark: '#1d232a' };

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark';
}

/** Тема на `<html data-theme>`: так её видят DaisyUI и `@custom-variant dark`. */
@Service()
export class ThemeStore {
  private readonly _theme = signal<Theme>('light');

  /** Отдельно от storage: persist() молча глотает ошибку записи (приватный режим Safari). */
  private hasExplicitChoice = false;

  readonly theme = this._theme.asReadonly();
  readonly isDark = computed(() => this._theme() === 'dark');

  /** До первого пейнта тему ставит inline-скрипт index.html; здесь она попадает в сигнал. */
  initTheme(): void {
    const stored = this.readStored();

    if (stored) {
      this.apply(stored);
    } else {
      this.apply(this.systemTheme());
      this.followSystem();
    }
  }

  toggle(): void {
    this.setTheme(this._theme() === 'light' ? 'dark' : 'light');
  }

  setTheme(theme: Theme): void {
    this.hasExplicitChoice = true;
    this.apply(theme);
    this.persist(theme);
  }

  /** Синхронно, а не через effect: атрибут обязан смениться сразу после клика, без цикла CD. */
  private apply(theme: Theme): void {
    this._theme.set(theme);
    this.applyToDocument(theme);
  }

  private applyToDocument(theme: Theme): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.setAttribute('data-theme', theme);
    // Две меты из разметки схлопываются в одну без `media`: иначе хром браузера слушал бы систему.
    const metas = document.querySelectorAll('meta[name="theme-color"]');
    metas.forEach((meta, index) => {
      if (index === 0) {
        meta.removeAttribute('media');
        meta.setAttribute('content', THEME_COLORS[theme]);
        return;
      }
      meta.remove();
    });
  }

  private readStored(): Theme | null {
    const raw: unknown = readStorage(STORAGE_KEYS.THEME);
    return isTheme(raw) ? raw : null;
  }

  private persist(theme: Theme): void {
    writeStorage(STORAGE_KEYS.THEME, theme);
  }

  private systemTheme(): Theme {
    return this.darkMediaQuery()?.matches ? 'dark' : 'light';
  }

  private followSystem(): void {
    const query = this.darkMediaQuery();
    query?.addEventListener('change', (event) => {
      // Флаг покрывает текущую сессию, storage — выбор, сделанный до перезагрузки.
      if (!this.hasExplicitChoice && !this.readStored()) {
        this.apply(event.matches ? 'dark' : 'light');
      }
    });
  }

  private darkMediaQuery(): MediaQueryList | null {
    try {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return null;
      }
      return window.matchMedia(DARK_QUERY);
    } catch {
      return null;
    }
  }
}
